import React, { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from '../styles.module.scss'

import { LoginModalTypeEnum } from '../login.enum'
import { ForgotPasswordInterface } from '../login.types'
import LoginModalContainer from '../login-modal-container/login-modal-container'
import { Button } from '../../../../components/atoms'
import { InputText } from '../../../../components/molecules'
import { ForgotPasswordDto } from '../../../../lib/graphql'
import { useDomainFromRouter } from '../../../../lib/hooks'
import { formatGqlError } from '../../../../utils/format-error'
import regex from '../../../../utils/regex'

export interface InputsForgot {
  email: string
}

const ForgotPassword = ({
  onForgotPassword,
  isLoading,
  setModalType,
  modalType: { options },
}: ForgotPasswordInterface) => {
  const [showForgotPasswordConfirm, setShowForgotPasswordConfirm] =
    useState<boolean>(false)
  const intl = useIntl()
  const domain = useDomainFromRouter()

  const {
    control,
    formState: { isValid },
    handleSubmit,
    setError,
  } = useForm<ForgotPasswordDto>({
    defaultValues: {
      email: '',
    },
    mode: 'onChange',
  })

  const onSubmit: SubmitHandler<ForgotPasswordDto> = async ({ email }) => {
    try {
      await onForgotPassword({ email })
      setShowForgotPasswordConfirm(true)
    } catch (e) {
      const gqlError = formatGqlError(e)
      if (gqlError?.message === 'EMAIL_NOT_FOUND') {
        setError('email', {
          type: 'string',
          message: 'Adresse e-mail non reconnue',
        })
      } else {
        setError('email', {
          type: 'string',
          message: "L'adresse mail saisie est introuvable",
        })
      }
    }
  }
  const canCloseOnClickOutside = options?.canCloseOnClickOutside

  const closeModal = () => {
    if (canCloseOnClickOutside === false) {
      setModalType({ type: LoginModalTypeEnum.Login, options })
    } else {
      setModalType({ type: LoginModalTypeEnum.Close })
    }
  }

  return (
    <LoginModalContainer
      closeModal={closeModal}
      title={intl.formatMessage({
        id: 'ui.ForgotPassword.resetPassword',
        defaultMessage: 'Réinitialisation du mot de passe',
      })}
      browserFooter={
        !domain
          ? {
              onClick: () =>
                setModalType({ type: LoginModalTypeEnum.Subscribe, options }),
              label: (
                <FormattedMessage
                  id='ui.forgotPassword.label.subscribe'
                  defaultMessage="Vous n'avez pas encore de compte ? Inscrivez-vous."
                />
              ),
              id: 'no-account-login',
            }
          : undefined
      }
      mobileFooter={{
        subButton: {
          label: intl.formatMessage({
            id: 'ui.forgotPassword.label.returnToLoginPage',
            defaultMessage: 'Retour à la page de connexion',
          }),
          onClick: closeModal,
        },
        button: {
          label: showForgotPasswordConfirm
            ? intl.formatMessage({
                id: 'ui.forgotPassword.mailSent',
                defaultMessage: 'Mail envoyé',
              })
            : intl.formatMessage({
                id: 'ui.forgotPassword.sendMail',
                defaultMessage: 'Envoyer',
              }),
          onClick: handleSubmit(onSubmit),
          disabled: !isValid || showForgotPasswordConfirm,
          isLoading,
          id: 'send-mail-forgot-password',
        },
      }}
    >
      <div className='px-0 md:px-5'>
        <p className={s.description}>
          {showForgotPasswordConfirm ? (
            <FormattedMessage
              id='ui.forgotPassword.forgotPasswordConfirmed'
              defaultMessage='Vous retrouverez le lien dans un e-mail en provenance de Naboo sur votre boîte de réception'
            />
          ) : (
            <FormattedMessage
              id='ui.forgotPassword.forgotPasswordNotConfirmed'
              defaultMessage='Entrez votre e-mail ci-dessous, nous vous enverrons un lien pour réinitialiser votre mot de passe.'
            />
          )}
        </p>
        <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
          <InputText
            control={control}
            name='email'
            label={
              showForgotPasswordConfirm ? (
                <FormattedMessage
                  id='ui.forgotPassword.destinationEmail'
                  defaultMessage='Adresse e-mail de destination'
                />
              ) : (
                <FormattedMessage
                  id='ui.forgotPassword.businessMail'
                  defaultMessage='E-mail professionnel'
                />
              )
            }
            rules={{
              required: intl.formatMessage({
                id: 'ui.forgotPassword.emailRequired',
                defaultMessage: 'Une adresse e-mail est requise.',
              }),
              pattern: {
                value: regex.email,
                message: intl.formatMessage({
                  id: 'ui.forgotPassword.wrongEmailFormat',
                  defaultMessage: "Respecter le bon format d'e-mail",
                }),
              },
            }}
            inputProps={{
              type: 'text',
              'aria-label': intl.formatMessage({
                id: 'ui.forgotPassword.enterEmail',
                defaultMessage: 'Entrez votre e-mail',
              }),
              'aria-required': 'true',
            }}
            placeholder={intl.formatMessage({
              id: 'ui.forgotPassword.enterEmail',
              defaultMessage: 'Entrez votre e-mail',
            })}
            disabled={showForgotPasswordConfirm}
            dataTestId='forgot-password-email-input'
          />

          <Button
            className='mt-5 hidden md:flex'
            loading={isLoading}
            type='submit'
            disabled={!isValid || showForgotPasswordConfirm}
            id='send-mail-forgot-password'
            data-testid='forgot-password-submit-button'
          >
            {showForgotPasswordConfirm ? (
              <FormattedMessage
                id='ui.forgotPassword.mailSent'
                defaultMessage='Mail envoyé'
              />
            ) : (
              <FormattedMessage
                id='ui.forgotPassword.sendMail'
                defaultMessage='Envoyer'
              />
            )}
          </Button>
        </form>
      </div>
    </LoginModalContainer>
  )
}

export default ForgotPassword
